import Api from './Api';
import onlyEligibleFilter from '../utils/only-eligible-filter';
import subscriptionCancelAt from '../utils/subscription';
import { APP } from './Constants';

export const login = async (values) => {
  const result = await Api.post('/admin/login', values);
  return result.data;
};

export const logout = async () => {
  Api.post('/admin/logout');
};

export const getProfile = async () => {
  const result = await Api.post('/admin/get-profile');
  return result.data;
};

export const fetch = async ({
  app,
  model,
  method,
  filter,
  sort,
  projection,
  skip = 0,
  limit = 100,
  resolveProfiles,
}) => {
  const result = await Api.post('/admin/fetch', {
    app,
    model,
    method,
    filter,
    sort,
    projection,
    skip,
    limit,
    ...(resolveProfiles ? { resolveProfiles: true } : {}),
  });
  return result.data;
};

const qToFilter = (q) => ({
  $or: [
    { name: { $regex: q, $options: 'i' } },
    { domain: { $regex: q, $options: 'i' } },
    { teamId: q },
    { state: q },
  ],
});
const fToFilter = (f, app) => ({
  ALL: {},
  FREE_TRIAL: { freeTrialStartDate: { $ne: null } },
  CUSTOMER: {
    $or: [
      { stripeSubscription: { $ne: null } },
      ...(app === 'seed' ? [] : [{ buddiesSubscription: true }]),
    ],
  },
})[f];

export const listWorkspaces = (app, f, limit, page, q = '') => fetch({
  app,
  model: 'SlackWorkspace',
  method: 'find',
  filter: {
    state: { $nin: ['jy_automation'] },
    $and: [
      fToFilter(f, app),
      ...(q ? [qToFilter(q)] : []),
    ],
  },
  sort: f === 'FREE_TRIAL' ? { freeTrialStartDate: -1 } : { _id: -1 },
  limit,
  skip: (page - 1) * limit,
});

export const countWorkspaces = (app, f, q) => fetch({
  app,
  model: 'SlackWorkspace',
  filter: {
    state: { $nin: ['jy_automation'] },
    ...fToFilter(f, app),
    ...(q ? qToFilter(q) : {}),
  },
  method: 'countDocuments',
});

// V2: Module filter by type + optional filter

// Most V2 apps share the default buddies bundle, so any non-null workspace
// subscription counts as a "buddies customer". SimplePerf, however, is sold as
// a separate bundle whose Stripe subscription carries metadata.app === 'buddies_perf'.
const BUDDIES_SUBSCRIPTION_APP = {
  simpleperf: 'buddies_perf',
};

// Mongo filter matching a workspace that holds the buddies subscription for `app`.
const paidWorkspaceFilter = (app) => {
  const metadataApp = BUDDIES_SUBSCRIPTION_APP[app];
  return metadataApp
    ? { 'stripeSubscription.metadata.app': metadataApp }
    : { stripeSubscription: { $ne: null } };
};

// Whether an (already fetched) workspace holds the buddies subscription for `app`.
const hasBuddiesSubscription = (app, workspace) => {
  const sub = workspace?.stripeSubscription;
  if (!sub) return false;
  const metadataApp = BUDDIES_SUBSCRIPTION_APP[app];
  return metadataApp ? sub.metadata?.app === metadataApp : true;
};

const fetchPaidWorkspaceIds = async (app) => {
  const paidWorkspaces = await fetch({
    app,
    model: 'Workspace',
    method: 'find',
    filter: paidWorkspaceFilter(app),
    projection: { _id: 1 },
    limit: 10000,
  });
  return (paidWorkspaces || []).map((w) => w._id);
};

const fToModuleFilter = async (app, f, moduleType) => {
  const base = { type: moduleType };
  if (f === 'FREE_TRIAL') return { ...base, freeTrialStartDate: { $ne: null } };
  if (f === 'FREE_FOREVER') return { ...base, freeForever: true };
  if (f === 'DIRECT_CUSTOMER') return { ...base, stripeSubscription: { $ne: null } };
  if (f === 'BUDDIES_CUSTOMER') {
    const wsIds = await fetchPaidWorkspaceIds(app);
    if (!wsIds.length) return { ...base, _id: null };
    return { ...base, workspaceId: { $in: wsIds } };
  }
  if (f === 'CUSTOMER') {
    const wsIds = await fetchPaidWorkspaceIds(app);
    return {
      ...base,
      $or: [
        { stripeSubscription: { $ne: null } },
        ...(wsIds.length ? [{ workspaceId: { $in: wsIds } }] : []),
      ],
    };
  }
  return base;
};

// V2: Resolve search query into a Module filter.
// Supports:
//   - exact ObjectId match on workspaceId, moduleId, integrationId
//   - exact match on integration platform id (Integration.platformTeamId)
//   - case-insensitive partial match on Workspace.name / Workspace.domain
const isObjectId = (s) => /^[a-f\d]{24}$/i.test(s);
const resolveV2SearchFilter = async (app, moduleType, q) => {
  if (!q) return {};
  const isOid = isObjectId(q);
  const [workspaces, integrationsByPlatform, integrationsById] = await Promise.all([
    fetch({
      app,
      model: 'Workspace',
      method: 'find',
      filter: { $or: [{ name: { $regex: q, $options: 'i' } }, { domain: { $regex: q, $options: 'i' } }] },
      projection: { _id: 1 },
      limit: 500,
    }),
    fetch({
      app,
      model: 'Integration',
      method: 'find',
      filter: { platformTeamId: q, type: { $ne: 'WEB' } },
      projection: { moduleId: 1 },
      limit: 500,
    }),
    isOid ? fetch({
      app,
      model: 'Integration',
      method: 'find',
      filter: { _id: q },
      projection: { moduleId: 1 },
      limit: 1,
    }) : Promise.resolve([]),
  ]);
  const wsIds = (workspaces || []).map((w) => w._id);
  const modIds = [...new Set([
    ...(integrationsByPlatform || []).map((i) => i.moduleId),
    ...(integrationsById || []).map((i) => i.moduleId),
  ].filter(Boolean))];
  const conditions = [
    ...(isOid ? [{ _id: q }, { workspaceId: q }] : []),
    ...(wsIds.length ? [{ workspaceId: { $in: wsIds } }] : []),
    ...(modIds.length ? [{ _id: { $in: modIds } }] : []),
  ];
  if (!conditions.length) return { _id: null }; // no matches
  return { $or: conditions };
};

// V2: platform user id of whoever installed an integration.
// Slack stores it as params.authedUserId; Teams has no authed user, so we use the
// installer of the first conversation (kept even once the conversation is no
// longer live). The result matches IntegrationUser.userId on both platforms.
const integrationInstallerUserId = (integration) => {
  if (!integration) return undefined;
  if (integration.type === 'TEAMS') {
    return (integration.params?.conversations || [])
      .find((c) => c.installerAadObjectId)?.installerAadObjectId;
  }
  return integration.params?.authedUserId;
};

// V2: Enrich modules with Integration + Workspace data
const enrichModules = async (app, modules) => {
  if (!modules.length) return [];
  const moduleIds = modules.map((m) => m._id);
  const workspaceIds = [...new Set(modules.map((m) => m.workspaceId).filter(Boolean))];

  const [integrations, workspaces] = await Promise.all([
    fetch({
      app,
      model: 'Integration',
      method: 'find',
      filter: { moduleId: { $in: moduleIds } },
      limit: 10000,
    }),
    workspaceIds.length ? fetch({
      app,
      model: 'Workspace',
      method: 'find',
      filter: { _id: { $in: workspaceIds } },
      limit: workspaceIds.length,
    }) : [],
  ]);

  const integrationsByModuleId = {};
  (integrations || []).forEach((i) => {
    if (!integrationsByModuleId[i.moduleId]) integrationsByModuleId[i.moduleId] = [];
    integrationsByModuleId[i.moduleId].push(i);
  });
  const workspaceById = {};
  (workspaces || []).forEach((w) => { workspaceById[w._id] = w; });

  const enriched = modules.map((mod) => {
    const mIntegrations = integrationsByModuleId[mod._id] || [];
    const nonWeb = mIntegrations.filter((i) => i.type !== 'WEB');
    const primary = nonWeb[0] || mIntegrations[0]; // primary used for countSeats / payment link
    const workspace = workspaceById[mod.workspaceId];
    const isLive = nonWeb.some((i) => i.status === 'ACTIVE');
    return {
      _id: mod._id,
      moduleId: mod._id,
      workspaceId: mod.workspaceId,
      type: mod.type,
      ref: mod.ref,
      freeTrialStartDate: mod.freeTrialStartDate,
      freeForever: mod.freeForever,
      setupDoneAt: mod.setupDoneAt,
      params: mod.params,
      stripeSubscription: mod.stripeSubscription,
      integrations: mIntegrations.map((i) => ({
        _id: i._id,
        type: i.type,
        status: i.status,
        platformTeamId: i.platformTeamId,
        authedUserId: integrationInstallerUserId(i),
        syncStatus: i.syncStatus,
      })),
      integrationId: primary?._id,
      platformTeamId: primary?.platformTeamId,
      status: isLive ? 'LIVE' : (primary?.status || 'UNINSTALLED'),
      installedAt: true, // V2 has no installedAt; set truthy to avoid defaultStatus PENDING logic
      installerUserId: integrationInstallerUserId(primary),
      name: workspace?.name,
      domain: workspace?.domain,
      discoverySource: workspace?.discoverySource,
      stripeCustomer: workspace?.stripeCustomer,
      buddiesSubscription: hasBuddiesSubscription(app, workspace),
      buddiesSubscriptionCancelAt: hasBuddiesSubscription(app, workspace)
        ? subscriptionCancelAt(workspace.stripeSubscription)
        : null,
    };
  });

  // Resolve installer platform-userId → IntegrationUser → User so the
  // Workspaces UI can link straight to the installer in the Users tab.
  const installerPairs = enriched
    .filter((m) => m.integrationId && m.installerUserId)
    .map((m) => ({ integrationId: m.integrationId, userId: m.installerUserId }));
  if (!installerPairs.length) return enriched;

  const installerIntegrationUsers = await fetch({
    app,
    model: 'IntegrationUser',
    method: 'find',
    filter: { $or: installerPairs },
    projection: { integrationId: 1, userId: 1 },
    limit: installerPairs.length,
  });
  const integrationUserByKey = new Map();
  (installerIntegrationUsers || []).forEach((iu) => {
    integrationUserByKey.set(`${iu.integrationId}::${iu.userId}`, iu);
  });

  // Pair each installer with their own workspace. A bare
  // `{ 'profiles.integrationUserId': { $in: [...] } }` matches no index prefix
  // (User is indexed on `{ workspaceId, profiles.integrationUserId }`) and so
  // collection-scans every User in the database. Pairing the two fields keeps
  // the lookup on that index — same shape as the `installerPairs` query above.
  const workspaceIdByIntegrationId = new Map(
    enriched.filter((m) => m.integrationId).map((m) => [String(m.integrationId), m.workspaceId]),
  );
  const installerUserPairs = (installerIntegrationUsers || [])
    .map((iu) => {
      const workspaceId = workspaceIdByIntegrationId.get(String(iu.integrationId));
      return workspaceId ? { workspaceId, 'profiles.integrationUserId': iu._id } : null;
    })
    .filter(Boolean);
  const installerUsers = installerUserPairs.length ? await fetch({
    app,
    model: 'User',
    method: 'find',
    filter: { $or: installerUserPairs },
    projection: {
      email: 1, profile: 1, profiles: 1, workspaceId: 1,
    },
    limit: installerUserPairs.length,
    resolveProfiles: true,
  }) : [];
  const userByIntegrationUserId = new Map();
  (installerUsers || []).forEach((u) => {
    (u.profiles || []).forEach((p) => {
      if (p.integrationUserId) userByIntegrationUserId.set(String(p.integrationUserId), u);
    });
  });

  enriched.forEach((m) => {
    if (!m.integrationId || !m.installerUserId) return;
    const iu = integrationUserByKey.get(`${m.integrationId}::${m.installerUserId}`);
    if (!iu) return;
    const u = userByIntegrationUserId.get(String(iu._id));
    if (!u) return;
    const rp = u.resolvedProfile;
    const resolvedName = rp?.displayName
      || [rp?.firstName, rp?.lastName].filter(Boolean).join(' ');
    const fallbackName = [u.profile?.firstName, u.profile?.lastName].filter(Boolean).join(' ');
    // eslint-disable-next-line no-param-reassign
    m.installer = {
      _id: u._id,
      email: u.email,
      name: resolvedName || fallbackName || u.email,
    };
  });

  return enriched;
};

// Build the Module filter behind the Workspaces list. Resolving it costs up to
// three requests (paid-workspace ids, workspace name/domain regex, integration
// lookups), so it is its own step: the list and count queries share one result
// instead of each resolving it, and paginating reuses it rather than redoing
// the search. Depends only on (app, f, q) — deliberately not on page/pageSize.
export const resolveWorkspacesV2Filter = async (app, f, q = '') => {
  const { moduleType } = APP[app];
  const [baseFilter, searchFilter] = await Promise.all([
    fToModuleFilter(app, f, moduleType),
    resolveV2SearchFilter(app, moduleType, q),
  ]);
  return { ...baseFilter, ...searchFilter };
};

export const listWorkspacesV2 = async (app, filter, f, limit, page) => {
  const modules = await fetch({
    app,
    model: 'Module',
    method: 'find',
    filter,
    sort: f === 'FREE_TRIAL' ? { freeTrialStartDate: -1 } : { _id: -1 },
    limit,
    skip: (page - 1) * limit,
  });
  return enrichModules(app, modules || []);
};

export const countWorkspacesV2 = async (app, filter) => fetch({
  app,
  model: 'Module',
  method: 'countDocuments',
  filter,
});

const qToUserFilter = (q) => ({
  $or: [
    { 'data.real_name': { $regex: q, $options: 'i' } },
    { 'data.profile.real_name': { $regex: q, $options: 'i' } },
    { 'data.profile.email': { $regex: q, $options: 'i' } },
    { 'data.profile.title': { $regex: q, $options: 'i' } },
    { userId: q },
  ],
});
const fToUserFilter = (f) => ({
  ALL: {},
  ELIGIBLE: onlyEligibleFilter,
  BOT: { 'data.is_bot': true },
})[f];

export const listUsers = (app, f, page, teamId, q = '') => fetch({
  app,
  model: 'SlackUser',
  filter: {
    ...(teamId ? { teamId } : {}),
    ...fToUserFilter(f),
    ...(q ? qToUserFilter(q) : {}),
  },
  method: 'find',
  sort: { _id: -1 },
  skip: (page - 1) * 20,
  limit: 20,
});

const fToUserFilterV2 = (f, moduleAdminIds = []) => {
  if (f === 'ACTIVE') {
    return { $or: [{ deactivatedAt: null }, { deactivatedAt: { $exists: false } }] };
  }
  if (f === 'DEACTIVATED') return { deactivatedAt: { $ne: null } };
  if (f === 'ADMIN') {
    return moduleAdminIds.length
      ? { $or: [{ role: 'ADMIN' }, { _id: { $in: moduleAdminIds } }] }
      : { role: 'ADMIN' };
  }
  return {};
};

// Per-platform IntegrationUser.data fields searched for name/title regex match.
// Adjust here when a new platform is added or fields drift in the V2 schema.
const INTEGRATION_USER_TEXT_FIELDS = {
  SLACK: [
    'data.real_name',
    'data.profile.real_name',
    'data.profile.display_name',
    'data.profile.title',
  ],
  TEAMS: ['data.displayName', 'data.givenName', 'data.surname', 'data.jobTitle'],
  PUMBLE: ['data.firstName', 'data.lastName', 'data.title'],
  WEB: ['data.name', 'data.title'],
};
const ALL_INTEGRATION_USER_TEXT_FIELDS = [
  ...new Set(Object.values(INTEGRATION_USER_TEXT_FIELDS).flat()),
];

// Builds the User-level filter for the V2 search box:
//  - exact match on User._id and platform user id (IntegrationUser.userId)
//  - regex match on User.email and on per-platform name/title fields stored
//    on IntegrationUser.data (scoped to this module's integrations).
const buildV2UserSearchFilter = async (app, q, scopeIntegrationIds = null) => {
  if (!q) return {};
  const isOid = isObjectId(q);
  const iuFilter = {
    ...(scopeIntegrationIds ? { integrationId: { $in: scopeIntegrationIds } } : {}),
    $or: [
      { userId: q },
      ...ALL_INTEGRATION_USER_TEXT_FIELDS.map((field) => ({
        [field]: { $regex: q, $options: 'i' },
      })),
    ],
  };
  const matchingIus = await fetch({
    app,
    model: 'IntegrationUser',
    method: 'find',
    filter: iuFilter,
    projection: { _id: 1 },
    limit: 1000,
  });
  const matchingIuIds = (matchingIus || []).map((iu) => iu._id);
  const conditions = [
    ...(isOid ? [{ _id: q }] : []),
    { email: { $regex: q, $options: 'i' } },
    ...(matchingIuIds.length
      ? [{ 'profiles.integrationUserId': { $in: matchingIuIds } }]
      : []),
  ];
  return { $or: conditions };
};

// V2: enrich each User with platforms[] = [{ type, platformTeamId }] resolved
// from User.profiles[].integrationUserId → IntegrationUser.integrationId → Integration.
// When `scopeIntegrationIds` is provided, platforms are filtered to those whose
// integration belongs to the set (used to scope to a single Module's integrations).
const enrichUsersWithPlatforms = async (app, users, scopeIntegrationIds = null) => {
  if (!users.length) return users;
  const integrationUserIds = [
    ...new Set(
      users.flatMap((u) => (u.profiles || []).map((p) => p.integrationUserId).filter(Boolean)),
    ),
  ];
  if (!integrationUserIds.length) return users.map((u) => ({ ...u, platforms: [] }));
  const integrationUsers = await fetch({
    app,
    model: 'IntegrationUser',
    method: 'find',
    filter: { _id: { $in: integrationUserIds } },
    projection: { integrationId: 1, userId: 1 },
    limit: integrationUserIds.length,
  });
  const integrationIds = [
    ...new Set((integrationUsers || []).map((iu) => iu.integrationId).filter(Boolean)),
  ];
  const integrations = integrationIds.length ? await fetch({
    app,
    model: 'Integration',
    method: 'find',
    filter: { _id: { $in: integrationIds } },
    projection: { type: 1, platformTeamId: 1 },
    limit: integrationIds.length,
  }) : [];
  const integrationById = {};
  (integrations || []).forEach((i) => { integrationById[i._id] = i; });
  const recordByIntegrationUserId = {};
  (integrationUsers || []).forEach((iu) => {
    const integ = integrationById[iu.integrationId];
    if (integ) recordByIntegrationUserId[iu._id] = { integ, platformUserId: iu.userId };
  });
  const scopeSet = scopeIntegrationIds
    ? new Set(scopeIntegrationIds.map(String))
    : null;
  return users.map((u) => {
    const platforms = (u.profiles || [])
      .map((p) => {
        const rec = recordByIntegrationUserId[p.integrationUserId];
        if (!rec) return null;
        const { integ, platformUserId } = rec;
        if (scopeSet && !scopeSet.has(String(integ._id))) return null;
        return {
          type: integ.type,
          platformTeamId: integ.platformTeamId,
          platformUserId,
          email: p.email,
          deactivatedAt: p.deactivatedAt,
        };
      })
      .filter(Boolean);
    return { ...u, platforms };
  });
};

// Build the User filter behind the Users list. Resolving the search half hits
// IntegrationUser with a per-platform regex `$or`, so — like the Workspaces
// filter above — it is its own step shared by the list and count queries and
// reused across pagination. Depends only on (app, f, workspaceId, q, ctx).
export const resolveUsersV2Filter = async (app, f, workspaceId, q = '', ctx = {}) => {
  const { scopeIntegrationIds = null, moduleAdminIds = [] } = ctx;
  const searchFilter = await buildV2UserSearchFilter(app, q, scopeIntegrationIds);
  return {
    ...(workspaceId ? { workspaceId } : {}),
    ...fToUserFilterV2(f, moduleAdminIds),
    ...searchFilter,
  };
};

export const listUsersV2 = async (app, filter, page, scopeIntegrationIds = null) => {
  const users = await fetch({
    app,
    model: 'User',
    method: 'find',
    filter,
    sort: { _id: -1 },
    skip: (page - 1) * 20,
    limit: 20,
    resolveProfiles: true,
  });
  return enrichUsersWithPlatforms(app, users || [], scopeIntegrationIds);
};

// V2: load Workspace + Module (by moduleType) + the module's integration ids,
// used by the Users tab to (a) show module admins and (b) scope platforms.
export const getWorkspaceModuleContextV2 = async (app, workspaceId) => {
  const moduleType = APP[app]?.moduleType;
  if (!workspaceId || !moduleType) return null;
  const [workspaces, modules] = await Promise.all([
    fetch({
      app,
      model: 'Workspace',
      method: 'find',
      filter: { _id: workspaceId },
      limit: 1,
    }),
    fetch({
      app,
      model: 'Module',
      method: 'find',
      filter: { workspaceId, type: moduleType },
      limit: 1,
    }),
  ]);
  const workspace = workspaces?.[0] || null;
  const moduleDoc = modules?.[0] || null;
  let moduleIntegrationIds = [];
  if (moduleDoc) {
    const integrations = await fetch({
      app,
      model: 'Integration',
      method: 'find',
      filter: { moduleId: moduleDoc._id },
      projection: { _id: 1 },
      limit: 1000,
    });
    moduleIntegrationIds = (integrations || []).map((i) => i._id);
  }
  return { workspace, module: moduleDoc, moduleIntegrationIds };
};

export const countUsersV2 = async (app, filter) => fetch({
  app,
  model: 'User',
  method: 'countDocuments',
  filter,
});

export const countUsers = (app, f, teamId, q = '') => fetch({
  app,
  model: 'SlackUser',
  filter: {
    ...(teamId ? { teamId } : {}),
    ...fToUserFilter(f),
    ...(q ? qToUserFilter(q) : {}),
  },
  method: 'countDocuments',
});

export const getStats = async (app, ids) => {
  const isV2 = (APP[app]?.version || 1) >= 2;
  if (isV2) {
    const result = await Api.post('/admin/stats-v2', { app, moduleIds: ids });
    return result.data;
  }
  const result = await Api.post('/admin/stats', { app, teamIds: ids });
  return result.data;
};

export const deleteWorkspace = async (app, entityId) => Api.post('/admin/delete-workspace', {
  app,
  entityId,
});

export const grantFreePlan = async (app, entityId, workspaceId) => Api.post('/admin/grant-free-plan', {
  app,
  entityId,
  ...(workspaceId ? { workspaceId } : {}),
});

export const countSeats = async (app, teamId, workspaceId) => {
  const payload = { app, teamId };
  if (workspaceId) payload.workspaceId = workspaceId;
  const result = await Api.post('/admin/count-seats', payload);
  const d = result.data;
  return typeof d === 'object' && d !== null && 'seats' in d ? d.seats : d;
};

export const getSlackAppData = async (appId) => {
  const result = await Api.post('/admin/slack-marketplace/get-app-data', {
    appId,
  });
  return result.data;
};

const resolveWorkspaceIds = async (app, teamIds, moduleType) => {
  if (!teamIds.length || !moduleType) return new Map();
  const integrations = await fetch({
    app,
    model: 'Integration',
    method: 'find',
    filter: { platformTeamId: { $in: teamIds }, type: 'SLACK' },
    projection: { platformTeamId: 1, moduleId: 1 },
  });
  const moduleIds = [...new Set((integrations || []).map((i) => i.moduleId).filter(Boolean))];
  if (!moduleIds.length) return new Map();
  const modules = await fetch({
    app,
    model: 'Module',
    method: 'find',
    filter: {
      _id: { $in: moduleIds },
      type: moduleType,
      freeTrialStartDate: { $ne: null },
    },
    projection: { workspaceId: 1 },
    limit: moduleIds.length,
  });
  const workspaceByModuleId = new Map(
    (modules || []).filter((m) => m.workspaceId).map((m) => [m._id, m.workspaceId]),
  );
  const map = new Map();
  (integrations || []).forEach((i) => {
    if (!i.platformTeamId || map.has(i.platformTeamId)) return;
    const wsId = workspaceByModuleId.get(i.moduleId);
    if (wsId) map.set(i.platformTeamId, wsId);
  });
  return map;
};

const mapCount100 = (id) => ({ id, count: 100 });
export const getKPIs = async (app) => {
  const { data } = await Api.post('/admin/kpis', {
    app,
  });
  if (app === 'seed') {
    data.freeTrials.currentPeriod.items = data.freeTrials.currentPeriod.teamIds
      .map(mapCount100);
    data.freeTrials.previousPeriod.items = data.freeTrials.previousPeriod.teamIds
      .map(mapCount100);
  } else {
    const isV2 = (APP[app]?.version || 1) >= 2;

    let teamIdToWorkspaceId = new Map();
    if (isV2) {
      const allTeamIds = [...new Set([
        ...data.freeTrials.currentPeriod.teamIds,
        ...data.freeTrials.previousPeriod.teamIds,
      ])];
      teamIdToWorkspaceId = await resolveWorkspaceIds(app, allTeamIds, APP[app]?.moduleType);
    }

    const countSeatsForId = (id) => {
      const wsId = teamIdToWorkspaceId.get(id);
      if (isV2 && !wsId) {
        // eslint-disable-next-line no-console
        console.warn(`[getKPIs] ${app}: could not resolve workspaceId for platformTeamId ${id} — skipping count-seats`);
        return Promise.resolve({ id, count: 0 });
      }
      return countSeats(app, id, wsId).then((count) => ({ id, count }));
    };

    const [
      currentPeriodSeats,
      previousPeriodSeats,
      currentPeriodSeatsMS,
      previousPeriodSeatsMS,
    ] = await Promise.all([
      Promise.all(data.freeTrials.currentPeriod.teamIds.map(countSeatsForId)),
      Promise.all(data.freeTrials.previousPeriod.teamIds.map(countSeatsForId)),
      ...(app === 'billy' ? [
        Promise.all(data.freeTrials.currentPeriod.tenantIds
          .map((id) => countSeats(app, id).then((count) => ({ id, count })))),
        Promise.all(data.freeTrials.previousPeriod.tenantIds
          .map((id) => countSeats(app, id).then((count) => ({ id, count })))),
      ] : []),
    ]);
    data.freeTrials.currentPeriod.items = [...currentPeriodSeats, ...(currentPeriodSeatsMS || [])];
    data.freeTrials.previousPeriod
      .items = [...previousPeriodSeats, ...(previousPeriodSeatsMS || [])];
    if (app === 'billy') {
      data.slack.freeTrials.currentPeriod.items = currentPeriodSeats;
      data.slack.freeTrials.previousPeriod.items = previousPeriodSeats;
      data.teams.freeTrials.currentPeriod.items = currentPeriodSeatsMS;
      data.teams.freeTrials.previousPeriod.items = previousPeriodSeatsMS;
    }
  }
  return data;
};

export const getBuddiesMRR = async () => {
  const result = await Api.post('/admin/mrr', {});
  return result.data;
};

// One row per Stripe customer, sorted by MRR desc. Assembled server-side: the
// bundle subscriptions live in a collection no /admin/fetch route reaches, and
// neither fetch layer exposes `aggregate`, so MRR cannot be sorted on here.
export const getPortfolio = async () => {
  const result = await Api.post('/admin/portfolio', {});
  return result.data;
};

export const getAllKPIs = async () => {
  const apps = Object.keys(APP);
  const [results, mrr] = await Promise.all([
    Promise.all(apps.sort().map(async (app) => {
      const data = await getKPIs(app);
      return {
        ...data,
        app,
      };
    })),
    getBuddiesMRR(),
  ]);
  return [
    {
      app: 'buddies',
      mrr,
      freeTrials: {
        currentPeriod: {
          start: results[0].freeTrials.currentPeriod.start,
          end: results[0].freeTrials.currentPeriod.end,
          count: 0,
          teamIds: [],
          items: [],
        },
        previousPeriod: {
          start: results[0].freeTrials.previousPeriod.start,
          end: results[0].freeTrials.previousPeriod.end,
          count: 0,
          teamIds: [],
          items: [],
        },
      },
    },
    ...results,
  ];
};
